import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import axios from 'axios';
import api from '../../services/api';
import { RootState } from '../../store';
import { AdventureSessionDetailDto, AdventureStepKey, AdventureStepStatus } from '../../types/adventure';
import { getAdventureBadgeIconSrc, getEquipmentIconSrc } from '../../utils/adventureIcons';
import { PressToTalkButton } from '../../components/common/PressToTalkButton';
import { useAudioTranscription } from '../../hooks/useAudioTranscription';
import {
  getContainedImageRect,
  getTeamMemberLabel,
  splitCanvasLabelLines,
} from '../../utils/adventureRewards';
import boyAvatar1 from '../../assets/adventure-avatars/boy-1.png';
import boyAvatar2 from '../../assets/adventure-avatars/boy-2.png';
import boyAvatar3 from '../../assets/adventure-avatars/boy-3.png';
import girlAvatar1 from '../../assets/adventure-avatars/girl-1.png';
import girlAvatar2 from '../../assets/adventure-avatars/girl-2.png';
import girlAvatar3 from '../../assets/adventure-avatars/girl-3.png';
import adventurerImage from '../../assets/adventure-teammates/adventurer.png';
import biologistImage from '../../assets/adventure-teammates/biologist.png';
import guideImage from '../../assets/adventure-teammates/guide.png';
import sisterImage from '../../assets/adventure-teammates/sister.png';
import cousinImage from '../../assets/adventure-teammates/cousin.png';
import classmateImage from '../../assets/adventure-teammates/classmate.png';
import desertSceneImage from '../../assets/adventure-scenes/desert.jpg';
import rainforestSceneImage from '../../assets/adventure-scenes/rainforest.jpeg';
import islandSceneImage from '../../assets/adventure-scenes/island.jpeg';
import caveSceneImage from '../../assets/adventure-scenes/cave.jpeg';
import glacierSceneImage from '../../assets/adventure-scenes/glacier.jpg';
import guideStarImage from '../../assets/adventure-guide/guide-star-transparent.png';
import { APP_CONFIG } from '../../config/appConfig';

// 类型定义
interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  order?: number;
}

interface AiFeedback {
  passed: boolean;
  score: number;
  strengths?: string[];
  improvements?: string[];
  encouragement?: string;
  recognitionSummary?: string;
  shortComment?: string;
  starRatings?: {
    imaginationReasonableness: number;
    dilemmaSpecificity: number;
    survivalMethodSpecificity: number;
  };
  medalTitle?: string;
}

interface DangerGuideChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface DangerGuideTeacherSummary {
  misconceptionTags: string[];
  summary: string;
  updatedAt: string;
}

interface DangerGuideChatState {
  turns: DangerGuideChatTurn[];
  teacherSummary?: DangerGuideTeacherSummary;
  lastInputMode?: 'voice' | 'text';
  lastAnswer?: string;
  verdict?: 'scientific' | 'partly_scientific' | 'not_scientific' | 'needs_more_info';
  sciencePoints?: string[];
  suggestion?: string;
  updatedAt?: string;
}

const AI_NAME = APP_CONFIG.aiName;
const AI_COMMENT_LABEL = `${AI_NAME}点评`;
const AI_RATING_LABEL = `${AI_NAME}星级`;
const AI_DONE_LABEL = `${AI_NAME}已评价`;
const AI_EXPAND_LABEL = `展开${AI_NAME}评价`;

// 步骤定义
enum Step {
  SELECT_AVATAR = 0,
  SELECT_CHARACTERS = 1,
  SELECT_SCENE = 2,
  SELECT_EQUIPMENT = 3,
  DESIGN_DANGER = 4,
  WRITE_ESCAPE = 5,
  COMPLETED = 6
}

const EDITABLE_STEPS = [
  Step.SELECT_AVATAR,
  Step.SELECT_CHARACTERS,
  Step.SELECT_SCENE,
  Step.SELECT_EQUIPMENT,
  Step.DESIGN_DANGER,
  Step.WRITE_ESCAPE,
] as const;

const STEP_KEY_BY_STEP: Record<number, AdventureStepKey> = {
  [Step.SELECT_AVATAR]: 'AVATAR',
  [Step.SELECT_CHARACTERS]: 'TEAM',
  [Step.SELECT_SCENE]: 'SCENE',
  [Step.SELECT_EQUIPMENT]: 'EQUIPMENT',
  [Step.DESIGN_DANGER]: 'DANGER',
  [Step.WRITE_ESCAPE]: 'ESCAPE',
};

const STEP_BY_STEP_KEY: Record<AdventureStepKey, Step> = {
  AVATAR: Step.SELECT_AVATAR,
  TEAM: Step.SELECT_CHARACTERS,
  SCENE: Step.SELECT_SCENE,
  EQUIPMENT: Step.SELECT_EQUIPMENT,
  DANGER: Step.DESIGN_DANGER,
  ESCAPE: Step.WRITE_ESCAPE,
};

const STEP_LABEL_BY_STEP: Record<number, string> = {
  [Step.SELECT_AVATAR]: '形象',
  [Step.SELECT_CHARACTERS]: '队友',
  [Step.SELECT_SCENE]: '场景',
  [Step.SELECT_EQUIPMENT]: '装备',
  [Step.DESIGN_DANGER]: '险情',
  [Step.WRITE_ESCAPE]: '逃生',
};

interface AvatarOption {
  id: string;
  url: string;
  group: 'boys' | 'girls';
  label: string;
}

const AVATARS: AvatarOption[] = [
  { id: 'boy-1', url: boyAvatar1, group: 'boys', label: '头像 1' },
  { id: 'boy-2', url: boyAvatar2, group: 'boys', label: '头像 2' },
  { id: 'boy-3', url: boyAvatar3, group: 'boys', label: '头像 3' },
  { id: 'girl-1', url: girlAvatar1, group: 'girls', label: '头像 4' },
  { id: 'girl-2', url: girlAvatar2, group: 'girls', label: '头像 5' },
  { id: 'girl-3', url: girlAvatar3, group: 'girls', label: '头像 6' },
];

interface CharacterOption {
  id: string;
  name: string;
  image: string;
}

const CHARACTERS_COL_A: CharacterOption[] = [
  { id: 'adventurer', name: '经验丰富的探险爱好者', image: adventurerImage },
  { id: 'biologist', name: '知识渊博的生物学家', image: biologistImage },
  { id: 'guide', name: '见多识广的向导', image: guideImage }
];

const CHARACTERS_COL_B: CharacterOption[] = [
  { id: 'sister', name: '好奇心强、性格活泼的妹妹', image: sisterImage },
  { id: 'cousin', name: '胆子大但行事鲁莽的表哥', image: cousinImage },
  { id: 'classmate', name: '心细而胆小的同学', image: classmateImage }
];

const SCENES = [
  { id: 'desert', name: '茫茫大漠', image: desertSceneImage },
  { id: 'rainforest', name: '热带雨林', image: rainforestSceneImage },
  { id: 'island', name: '海中荒岛', image: islandSceneImage },
  { id: 'cave', name: '幽深洞穴', image: caveSceneImage },
  { id: 'glacier', name: '冰川', image: glacierSceneImage }
];

const getSceneGroupBySelection = (sceneId: string | null, customSceneValue: string) => {
  if (sceneId === 'custom' || customSceneValue.trim()) {
    return 'custom';
  }

  switch (sceneId) {
    case 'desert':
    case 'rainforest':
    case 'island':
    case 'cave':
    case 'glacier':
      return sceneId;
    default:
      return 'custom';
  }
};

const EQUIPMENT = [
  { id: 'compass', name: '指南针', icon: getEquipmentIconSrc('compass') },
  { id: 'map', name: '地图', icon: getEquipmentIconSrc('map') },
  { id: 'water', name: '饮用水', icon: getEquipmentIconSrc('water') },
  { id: 'food', name: '食物', icon: getEquipmentIconSrc('food') },
  { id: 'medicine', name: '药品', icon: getEquipmentIconSrc('medicine') },
  { id: 'tent', name: '帐篷', icon: getEquipmentIconSrc('tent') }
];

const ADVENTURE_BADGE_ALIASES = {
  preparation: ['整装待发'],
  team: ['慧眼识人', '组队勋章'],
  equipment: ['精装上阵', '启程勋章', '生存勋章'],
  danger: ['巧设险局', '勇气勋章'],
  escape: ['逃生高手'],
} as const;

const findBadgeByNames = (availableBadges: Badge[], names: readonly string[]) =>
  availableBadges.find((badge) => names.includes(badge.name));

const LEGACY_BADGE_STEP_ORDER: AdventureStepKey[] = ['TEAM', 'EQUIPMENT', 'DANGER', 'ESCAPE'];
const EXPANDED_BADGE_STEP_ORDER: AdventureStepKey[] = ['TEAM', 'SCENE', 'EQUIPMENT', 'DANGER', 'ESCAPE'];

const getAdventureBadgeStepOrder = (availableBadges: Badge[]) =>
  availableBadges.length >= EXPANDED_BADGE_STEP_ORDER.length ? EXPANDED_BADGE_STEP_ORDER : LEGACY_BADGE_STEP_ORDER;

const getBadgesSortedByOrder = (availableBadges: Badge[]) =>
  [...availableBadges].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const findBadgeForAdventureStep = (availableBadges: Badge[], stepKey: AdventureStepKey) => {
  const unifiedPreparationBadge = findBadgeByNames(availableBadges, ADVENTURE_BADGE_ALIASES.preparation);

  if (unifiedPreparationBadge && ['TEAM', 'SCENE', 'EQUIPMENT'].includes(stepKey)) {
    return unifiedPreparationBadge;
  }

  if (stepKey === 'TEAM') {
    return findBadgeByNames(availableBadges, ADVENTURE_BADGE_ALIASES.team);
  }

  if (stepKey === 'EQUIPMENT') {
    return findBadgeByNames(availableBadges, ADVENTURE_BADGE_ALIASES.equipment);
  }

  if (stepKey === 'DANGER') {
    return findBadgeByNames(availableBadges, ADVENTURE_BADGE_ALIASES.danger);
  }

  if (stepKey === 'ESCAPE') {
    return findBadgeByNames(availableBadges, ADVENTURE_BADGE_ALIASES.escape);
  }

  const orderedBadges = getBadgesSortedByOrder(availableBadges);
  const stepOrder = getAdventureBadgeStepOrder(orderedBadges);
  const stepIndex = stepOrder.indexOf(stepKey);

  if (stepIndex >= 0 && orderedBadges[stepIndex]) {
    return orderedBadges[stepIndex];
  }

  return undefined;
};

const getEarnedBadgesFromStepStatuses = ({
  availableBadges,
  statuses,
}: {
  availableBadges: Badge[];
  statuses: Partial<Record<AdventureStepKey, AdventureStepStatus>>;
}) => {
  const nextEarned: Badge[] = [];
  const seenBadgeIds = new Set<string>();

  (['TEAM', 'SCENE', 'EQUIPMENT', 'DANGER', 'ESCAPE'] as AdventureStepKey[]).forEach((stepKey) => {
    if (statuses[stepKey] !== 'COMPLETED') {
      return;
    }

    const badge = findBadgeForAdventureStep(availableBadges, stepKey);
    if (!badge || seenBadgeIds.has(badge.id)) {
      return;
    }

    seenBadgeIds.add(badge.id);
    nextEarned.push(badge);
  });

  return nextEarned;
};

const getSelectedSceneName = (selectedScene: string | null, customScene: string) =>
  selectedScene === 'custom'
    ? customScene.trim()
    : SCENES.find((scene) => scene.id === selectedScene)?.name ?? '';

const getSelectedEquipmentNames = (selectedEquipment: string[], customEquipment: string) => {
  const presetEquipmentNames = selectedEquipment
    .map((itemId) => EQUIPMENT.find((item) => item.id === itemId)?.name)
    .filter((name): name is string => Boolean(name));

  const customEquipmentName = customEquipment.trim();
  return customEquipmentName ? [...presetEquipmentNames, customEquipmentName] : presetEquipmentNames;
};

const getStepFromStepKey = (stepKey: AdventureStepKey): Step => STEP_BY_STEP_KEY[stepKey];

const getFurthestAccessibleStepFromSession = (detail: AdventureSessionDetailDto): Step => {
  if (detail.status === 'COMPLETED') {
    return Step.COMPLETED;
  }

  const sessionCurrentStep = getStepFromStepKey(detail.currentStep);

  return detail.steps.reduce<Step>((furthest, step) => {
    const mappedStep = getStepFromStepKey(step.stepKey);

    if (step.status === 'COMPLETED') {
      return Math.max(
        furthest,
        Math.min(mappedStep + 1, Step.WRITE_ESCAPE) as Step
      ) as Step;
    }

    if (step.status === 'IN_PROGRESS' || step.status === 'STALE') {
      return Math.max(furthest, mappedStep) as Step;
    }

    return furthest;
  }, sessionCurrentStep);
};

const getStepIndicatorClassName = ({
  isActive,
  isCompleted,
  isAccessible,
}: {
  isActive: boolean;
  isCompleted: boolean;
  isAccessible: boolean;
}) => {
  if (!isAccessible) {
    return 'border-stone-200 bg-stone-100 opacity-60 cursor-not-allowed';
  }

  if (isActive) {
    return 'border-amber-500 bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)] cursor-default';
  }

  if (isCompleted) {
    return 'border-emerald-500 bg-emerald-500 cursor-default';
  }

  return 'border-slate-300 bg-slate-200 cursor-default';
};

const SUMMARY_HIGHLIGHT_CLASSES = {
  student: 'rounded-md bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800',
  teammateA: 'rounded-md bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800',
  teammateB: 'rounded-md bg-sky-100 px-1.5 py-0.5 font-semibold text-sky-800',
  scene: 'rounded-md bg-fuchsia-100 px-1.5 py-0.5 font-semibold text-fuchsia-800',
  equipment: 'rounded-md bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-800',
} as const;

const MAX_DANGER_UPLOAD_DIMENSION = 2200;
const MAX_DANGER_UPLOAD_BYTES = 2 * 1024 * 1024;
const DANGER_UPLOAD_JPEG_QUALITY = 0.9;

const loadImageFromFile = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load upload image'));
    };

    image.src = objectUrl;
  });

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Failed to read preview data url'));
    };

    reader.onerror = () => reject(new Error('Failed to read preview file'));
    reader.readAsDataURL(file);
  });

const prepareDangerUploadFile = async (file: File) => {
  if (
    file.size <= MAX_DANGER_UPLOAD_BYTES ||
    typeof document === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return file;
  }

  try {
    const image = await loadImageFromFile(file);
    const longestSide = Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height);
    const scale = longestSide > MAX_DANGER_UPLOAD_DIMENSION
      ? MAX_DANGER_UPLOAD_DIMENSION / longestSide
      : 1;
    const targetWidth = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    const targetHeight = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      return file;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const compressedBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', DANGER_UPLOAD_JPEG_QUALITY);
    });

    if (!compressedBlob) {
      return file;
    }

    if (compressedBlob.size >= file.size) {
      return file;
    }

    const nextName = file.name.replace(/\.[^.]+$/, '') || 'danger-upload';
    return new File([compressedBlob], `${nextName}.jpg`, {
      type: 'image/jpeg',
    });
  } catch (error) {
    console.error('Failed to compress danger upload', error);
    return file;
  }
};

const normalizeDangerPreviewValue = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('blob:')) {
    return null;
  }

  return trimmed;
};

const getDangerUploadErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const backendError = error.response?.data?.error;

    if (status === 401 || backendError === 'Token expired' || backendError === 'Invalid token') {
      return '登录状态已失效，请重新登录后再试。';
    }

    if (status === 413) {
      return '图片太大了，请重新拍一张或压缩后再上传。';
    }

    if (typeof backendError === 'string' && backendError.includes('No image file')) {
      return '没有收到图片文件，请重新上传。';
    }

    if (status && status >= 500) {
      return '图片识别服务暂时不可用，请稍后再试。';
    }
  }

  return '上传失败，请检查网络后重试。';
};

const buildFeedbackNarrative = (
  feedback: AiFeedback,
  options?: {
    compact?: boolean;
  }
) => {
  const strengths = (feedback.strengths ?? []).filter(Boolean);
  const improvements = (feedback.improvements ?? []).filter(Boolean);
  const segments: string[] = [];
  const compact = options?.compact ?? false;
  const recognitionSummary = feedback.recognitionSummary?.trim() || '';
  const primaryComment = (feedback.shortComment?.trim() || feedback.encouragement?.trim() || '').trim();
  const normalizeLine = (value: string) => value.replace(/[\s，。！？!?,、；;：“”"'`]/g, '');

  if (recognitionSummary && normalizeLine(recognitionSummary) !== normalizeLine(primaryComment)) {
    segments.push(recognitionSummary);
  }

  if (primaryComment) {
    segments.push(primaryComment);
  }

  if (compact) {
    return segments;
  }

  if (strengths.length > 0) {
    segments.push(`亮点：${strengths.join('；')}`);
  }

  if (improvements.length > 0) {
    segments.push(`建议：${improvements.join('；')}`);
  }

  return segments;
};

const STAR_LABELS = [
  { key: 'imaginationReasonableness', label: '想象丰富合理' },
  { key: 'dilemmaSpecificity', label: '困境描写具体' },
  { key: 'survivalMethodSpecificity', label: '求生方法具体' },
] as const;

const renderStarRatings = (feedback: AiFeedback) => {
  if (!feedback.starRatings) {
    return null;
  }

  return (
    <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-left">
      <p className="text-sm font-bold text-amber-800">{AI_RATING_LABEL}</p>
      <div className="mt-3 space-y-2">
        {STAR_LABELS.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-3 text-sm text-slate-700">
            <span>{label}</span>
            <span className="font-medium tracking-[0.2em] text-amber-500">
              {'★'.repeat(feedback.starRatings?.[key] ?? 0)}
              <span className="tracking-normal text-slate-300">
                {'☆'.repeat(5 - (feedback.starRatings?.[key] ?? 0))}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const renderFeedbackNarrativeCard = (
  feedback: AiFeedback,
  options?: {
    compact?: boolean;
    hideTitle?: boolean;
  }
) => {
  const narrative = buildFeedbackNarrative(feedback, options);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 text-left">
      {!options?.hideTitle && <p className="text-sm font-bold text-slate-500">{AI_COMMENT_LABEL}</p>}
      <div className="mt-3 space-y-3">
        {narrative.map((line, index) => (
          <p key={`${line}-${index}`} className="text-sm leading-7 text-slate-700">
            {line}
          </p>
        ))}
      </div>
      {renderStarRatings(feedback)}
    </div>
  );
};

const getStepPreviewImage = ({
  stepKey,
  generatedImage,
  dangerPreview,
  escapePreview,
}: {
  stepKey: AdventureStepKey;
  generatedImage: string;
  dangerPreview: string | null;
  escapePreview: string | null;
}) => {
  if (stepKey === 'TEAM') {
    return generatedImage;
  }

  if (stepKey === 'DANGER') {
    return dangerPreview ?? '';
  }

  if (stepKey === 'ESCAPE') {
    return escapePreview ?? '';
  }

  return '';
};

const normalizeStoredFeedback = (value: unknown): AiFeedback | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybeFeedback = value as Partial<AiFeedback>;
  const normalizedShortComment =
    typeof maybeFeedback.shortComment === 'string' && maybeFeedback.shortComment.trim()
      ? maybeFeedback.shortComment.trim()
      : undefined;
  const normalizedEncouragement =
    typeof maybeFeedback.encouragement === 'string' && maybeFeedback.encouragement.trim()
      ? maybeFeedback.encouragement.trim()
      : normalizedShortComment;
  const normalizedRecognitionSummary =
    typeof maybeFeedback.recognitionSummary === 'string' && maybeFeedback.recognitionSummary.trim()
      ? maybeFeedback.recognitionSummary.trim()
      : undefined;

  if (!normalizedEncouragement && !normalizedShortComment) {
    return null;
  }

  return {
    passed: typeof maybeFeedback.passed === 'boolean' ? maybeFeedback.passed : true,
    score: typeof maybeFeedback.score === 'number' ? maybeFeedback.score : 0,
    strengths: Array.isArray(maybeFeedback.strengths) ? maybeFeedback.strengths.filter(Boolean) as string[] : [],
    improvements: Array.isArray(maybeFeedback.improvements)
      ? maybeFeedback.improvements.filter(Boolean) as string[]
      : [],
    recognitionSummary: normalizedRecognitionSummary,
    shortComment: normalizedShortComment ?? normalizedEncouragement,
    encouragement: normalizedEncouragement,
    starRatings:
      maybeFeedback.starRatings && typeof maybeFeedback.starRatings === 'object'
        ? {
          imaginationReasonableness:
            Number((maybeFeedback.starRatings as any).imaginationReasonableness ?? (maybeFeedback.starRatings as any).crisisProgression) || 0,
          dilemmaSpecificity:
            Number((maybeFeedback.starRatings as any).dilemmaSpecificity ?? (maybeFeedback.starRatings as any).detailMagnification) || 0,
          survivalMethodSpecificity:
            Number((maybeFeedback.starRatings as any).survivalMethodSpecificity ?? (maybeFeedback.starRatings as any).teamworkCoordination) || 0,
        }
        : undefined,
    medalTitle: typeof maybeFeedback.medalTitle === 'string' ? maybeFeedback.medalTitle : undefined,
  };
};

const normalizeDangerGuideChatState = (value: unknown): DangerGuideChatState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const turns = Array.isArray(record.turns)
    ? record.turns
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const turn = item as Record<string, unknown>;
        if ((turn.role !== 'user' && turn.role !== 'assistant') || typeof turn.content !== 'string' || !turn.content.trim()) {
          return null;
        }

        return {
          role: turn.role,
          content: turn.content.trim(),
        } as DangerGuideChatTurn;
      })
      .filter((item): item is DangerGuideChatTurn => Boolean(item))
    : [];

  const teacherSummary =
    record.teacherSummary && typeof record.teacherSummary === 'object'
      ? (() => {
        const summary = record.teacherSummary as Record<string, unknown>;
        return {
          misconceptionTags: Array.isArray(summary.misconceptionTags)
            ? summary.misconceptionTags.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).slice(0, 3)
            : [],
          summary: typeof summary.summary === 'string' ? summary.summary.trim() : '',
          updatedAt: typeof summary.updatedAt === 'string' ? summary.updatedAt : '',
        };
      })()
      : undefined;

  return {
    turns,
    teacherSummary: teacherSummary && teacherSummary.summary ? teacherSummary : undefined,
    lastInputMode: record.lastInputMode === 'voice' ? 'voice' : record.lastInputMode === 'text' ? 'text' : undefined,
    lastAnswer: typeof record.lastAnswer === 'string' ? record.lastAnswer.trim() : undefined,
    verdict:
      record.verdict === 'scientific' ||
      record.verdict === 'partly_scientific' ||
      record.verdict === 'not_scientific' ||
      record.verdict === 'needs_more_info'
        ? record.verdict
        : undefined,
    sciencePoints: Array.isArray(record.sciencePoints)
      ? record.sciencePoints.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).slice(0, 3)
      : [],
    suggestion: typeof record.suggestion === 'string' ? record.suggestion.trim() : undefined,
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : undefined,
  };
};

export const AdventureWorkspace: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionId = searchParams.get('sessionId');
  const locationState = (location.state as {
    reopenRewardModal?: boolean;
    rewardStepKey?: AdventureStepKey;
    returnStepKey?: AdventureStepKey;
  } | null) ?? null;

  // 状态管理
  const [currentStep, setCurrentStep] = useState<Step>(Step.SELECT_AVATAR);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [earnedBadges, setEarnedBadges] = useState<Badge[]>([]);
  const [rewardModal, setRewardModal] = useState<{ show: boolean; badge?: Badge; photo?: string; step: Step } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecognizingDanger, setIsRecognizingDanger] = useState(false);
  const [isRecognizingEscape, setIsRecognizingEscape] = useState(false);
  const [feedback, setFeedback] = useState<AiFeedback | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [furthestStepReached, setFurthestStepReached] = useState<Step>(Step.SELECT_AVATAR);
  const [isFeedbackRailCollapsed, setIsFeedbackRailCollapsed] = useState(false);

  // 选择状态
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [selectedCharA, setSelectedCharA] = useState<string | null>(null);
  const [selectedCharB, setSelectedCharB] = useState<string | null>(null);
  const [selectedScene, setSelectedScene] = useState<string | null>(null);
  const [customScene, setCustomScene] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [customEquipment, setCustomEquipment] = useState('');
  const [preparationSummary, setPreparationSummary] = useState('');
  const [dangerPreview, setDangerPreview] = useState<string | null>(null);
  const [dangerDescription, setDangerDescription] = useState('');
  const [escapePreview, setEscapePreview] = useState<string | null>(null);
  const [escapeDescription, setEscapeDescription] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string>('');
  const [preparationPosterSceneName, setPreparationPosterSceneName] = useState('');
  const [isDangerGuideOpen, setIsDangerGuideOpen] = useState(false);
  const [isDangerGuideSubmitting, setIsDangerGuideSubmitting] = useState(false);
  const [dangerGuideDraft, setDangerGuideDraft] = useState('');
  const [dangerGuideLastInputMode, setDangerGuideLastInputMode] = useState<'voice' | 'text'>('text');
  const [dangerGuideState, setDangerGuideState] = useState<DangerGuideChatState | null>(null);
  const [dangerGuideError, setDangerGuideError] = useState('');

  // 理由输入
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSessionHydrated, setIsSessionHydrated] = useState<boolean>(() => !Boolean(sessionId));
  const [sessionLoadError, setSessionLoadError] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [stepStatuses, setStepStatuses] = useState<Record<AdventureStepKey, AdventureStepStatus>>({
    AVATAR: 'NOT_STARTED',
    TEAM: 'NOT_STARTED',
    SCENE: 'NOT_STARTED',
    EQUIPMENT: 'NOT_STARTED',
    DANGER: 'NOT_STARTED',
    ESCAPE: 'NOT_STARTED',
  });
  const [stepFeedbacks, setStepFeedbacks] = useState<Record<AdventureStepKey, AiFeedback | null>>({
    AVATAR: null,
    TEAM: null,
    SCENE: null,
    EQUIPMENT: null,
    DANGER: null,
    ESCAPE: null,
  });
  const saveTimerRef = useRef<number | null>(null);
  const isHydratingSessionRef = useRef(false);
  const isRebuildingCompletedPhotoRef = useRef(false);
  const latestSessionDetailRef = useRef<AdventureSessionDetailDto | null>(null);
  const hasRestoredRewardModalRef = useRef(false);
  const restoredReturnStepRef = useRef('');
  const earnedBadgeIds = new Set(earnedBadges.map((badge) => badge.id));
  const badgeSlots = badges;
  const selectedCharAName = CHARACTERS_COL_A.find((char) => char.id === selectedCharA)?.name ?? '第一位队友';
  const selectedCharBName = CHARACTERS_COL_B.find((char) => char.id === selectedCharB)?.name ?? '第二位队友';
  const selectedSceneName = getSelectedSceneName(selectedScene, customScene);
  const selectedEquipmentNames = getSelectedEquipmentNames(selectedEquipment, customEquipment);
  const studentDisplayName = user?.username ?? '你';
  const sceneSummaryName = selectedSceneName || '探险地点';
  const completedPosterSceneName = preparationPosterSceneName || selectedSceneName;
  const equipmentSummaryText = selectedEquipmentNames.join('、') || '装备';
  const currentStepKey = currentStep === Step.COMPLETED ? 'ESCAPE' : STEP_KEY_BY_STEP[currentStep];
  const currentStepFeedback = stepFeedbacks[currentStepKey] ?? feedback;
  const shouldCompactCurrentStepFeedback = currentStepKey === 'TEAM' || currentStepKey === 'DANGER';
  const currentStepPreview = getStepPreviewImage({
    stepKey: currentStepKey,
    generatedImage,
    dangerPreview,
    escapePreview,
  });
  const showCompletedFeedbackRail =
    currentStep !== Step.COMPLETED &&
    currentStepKey !== 'DANGER' &&
    currentStepKey !== 'ESCAPE' &&
    stepStatuses[currentStepKey] === 'COMPLETED' &&
    Boolean(currentStepFeedback) &&
    !rewardModal;
  const rewardModalFeedback =
    rewardModal ? feedback ?? stepFeedbacks[STEP_KEY_BY_STEP[rewardModal.step]] ?? null : null;
  const rewardPreviewSrc =
    rewardModal?.step === Step.DESIGN_DANGER
      ? dangerPreview?.trim() || ''
      : rewardModal?.step === Step.WRITE_ESCAPE
        ? escapePreview?.trim() || ''
        : '';
  const {
    status: summaryTranscriptionStatus,
    errorMessage: summaryTranscriptionError,
    isSupported: isSummarySpeechSupported,
    startRecording: startSummaryRecording,
    stopRecording: stopSummaryRecording,
    cancelRecording: cancelSummaryRecording,
  } = useAudioTranscription({
    context: 'adventure-preparation',
    onTranscription: (text) => {
      if (currentStep === Step.SELECT_EQUIPMENT) {
        setPreparationSummary((prev) => prev + text);
      }
    },
  });
  const {
    status: customEquipmentTranscriptionStatus,
    errorMessage: customEquipmentTranscriptionError,
    isSupported: isCustomEquipmentSpeechSupported,
    startRecording: startCustomEquipmentRecording,
    stopRecording: stopCustomEquipmentRecording,
    cancelRecording: cancelCustomEquipmentRecording,
  } = useAudioTranscription({
    context: 'adventure-custom-equipment',
    onTranscription: (text) => {
      if (currentStep === Step.SELECT_EQUIPMENT) {
        setCustomEquipment((prev) => `${prev}${text}`);
      }
    },
  });
  const {
    status: customSceneTranscriptionStatus,
    errorMessage: customSceneTranscriptionError,
    isSupported: isCustomSceneSpeechSupported,
    startRecording: startCustomSceneRecording,
    stopRecording: stopCustomSceneRecording,
    cancelRecording: cancelCustomSceneRecording,
  } = useAudioTranscription({
    context: 'adventure-custom-scene',
    onTranscription: (text) => {
      if (currentStep === Step.SELECT_SCENE) {
        setCustomScene((prev) => `${prev}${text}`);
      }
    },
  });
  const {
    status: dangerGuideTranscriptionStatus,
    errorMessage: dangerGuideTranscriptionError,
    isSupported: isDangerGuideSpeechSupported,
    startRecording: startDangerGuideRecording,
    stopRecording: stopDangerGuideRecording,
    cancelRecording: cancelDangerGuideRecording,
  } = useAudioTranscription({
    context: 'adventure-danger-guide',
    onTranscription: (text) => {
      if (currentStep === Step.DESIGN_DANGER) {
        setDangerGuideLastInputMode('voice');
        setDangerGuideDraft((prev) => `${prev}${text}`);
      }
    },
  });

  const getBadgeNameByStepKey = (stepKey: AdventureStepKey) => {
    if (stepKey === 'TEAM') {
      return findBadgeForAdventureStep(badges, 'TEAM')?.name ?? '慧眼识人';
    }

    if (stepKey === 'SCENE') {
      return findBadgeForAdventureStep(badges, 'SCENE')?.name ?? '探险场景';
    }

    if (stepKey === 'EQUIPMENT') {
      return findBadgeForAdventureStep(badges, 'EQUIPMENT')?.name ?? '精装上阵';
    }

    if (stepKey === 'DANGER') {
      return findBadgeForAdventureStep(badges, 'DANGER')?.name ?? '巧设险局';
    }

    if (stepKey === 'ESCAPE') {
      return findBadgeForAdventureStep(badges, 'ESCAPE')?.name ?? '逃生高手';
    }

    return STEP_LABEL_BY_STEP[EDITABLE_STEPS.find((step) => STEP_KEY_BY_STEP[step] === stepKey) ?? Step.SELECT_AVATAR];
  };

  const getDependentStepKeys = (step: AdventureStepKey): AdventureStepKey[] => {
    if (step === 'SCENE') {
      return ['EQUIPMENT', 'DANGER', 'ESCAPE'];
    }

    if (step === 'TEAM' || step === 'EQUIPMENT') {
      return ['DANGER', 'ESCAPE'];
    }

    if (step === 'DANGER') {
      return ['ESCAPE'];
    }

    return [];
  };

  const markDependentStepsStale = (step: AdventureStepKey) => {
    const dependents = getDependentStepKeys(step);

    if (dependents.length === 0) {
      return;
    }

    setStepStatuses((prev) => {
      const next = { ...prev };
      dependents.forEach((key) => {
        if (prev[key] !== 'NOT_STARTED') {
          next[key] = 'STALE';
        }
      });
      return next;
    });
  };

  const getDraftPayloadForStep = (step: AdventureStepKey) => {
    switch (step) {
      case 'AVATAR':
        return {
          selectedAvatar,
          avatarLabel: AVATARS.find((avatar) => avatar.id === selectedAvatar)?.label ?? '',
        };
      case 'TEAM':
        return {
          selectedCharA,
          selectedCharB,
          selectedCharAName,
          selectedCharBName,
        };
      case 'SCENE':
        return {
          selectedScene,
          customScene,
          selectedSceneName,
        };
      case 'EQUIPMENT':
        return {
          selectedAvatar,
          selectedEquipment,
          customEquipment,
          selectedEquipmentNames,
          selectedSceneName,
          voiceSummary: preparationSummary,
        };
      case 'DANGER':
        return {
          dangerDescription,
          selectedSceneName,
          selectedEquipmentNames,
          guideChat: dangerGuideState,
        };
      case 'ESCAPE':
        return {
          escapeDescription,
          selectedSceneName,
          selectedEquipmentNames,
        };
      default:
        return {};
    }
  };

  const getTeamDraftPayload = () => ({
    selectedCharA,
    selectedCharB,
    selectedCharAName,
    selectedCharBName,
  });

  const getHighestAccessibleStep = () => furthestStepReached;

  // 本地存储键
  const storageKey = `adventure_progress_${user?.id}_${assignmentId}`;
  const clearPendingDraftSave = () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  // 保存进度到本地存储
  useEffect(() => {
    if (!user || !assignmentId || sessionId) return;

    const progress = {
      currentStep,
      selectedAvatar,
      selectedCharA,
      selectedCharB,
      selectedScene,
      customScene,
      selectedEquipment,
      customEquipment,
      dangerDescription,
      dangerPreview,
      escapeDescription,
      escapePreview,
      generatedImage,
      preparationPosterSceneName,
      earnedBadges, // 也保存已获得的勋章，防止刷新后丢失显示
      preparationSummary,
    };

    localStorage.setItem(storageKey, JSON.stringify(progress));
  }, [
    currentStep,
    selectedAvatar,
    selectedCharA,
    selectedCharB,
    selectedScene,
    customScene,
    selectedEquipment,
    customEquipment,
    dangerDescription,
    dangerPreview,
    escapeDescription,
    escapePreview,
    generatedImage,
    preparationPosterSceneName,
    earnedBadges,
    preparationSummary,
    user,
    assignmentId,
    sessionId,
  ]);

  // 加载进度
  useEffect(() => {
    if (!user || !assignmentId || sessionId) return;

    const savedProgress = localStorage.getItem(storageKey);
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        if (parsed.currentStep !== undefined) setCurrentStep(parsed.currentStep);
        if (parsed.selectedAvatar) setSelectedAvatar(parsed.selectedAvatar);
        if (parsed.selectedCharA) setSelectedCharA(parsed.selectedCharA);
        if (parsed.selectedCharB) setSelectedCharB(parsed.selectedCharB);
        if (parsed.selectedScene) setSelectedScene(parsed.selectedScene);
        if (parsed.customScene) setCustomScene(parsed.customScene);
        if (parsed.selectedEquipment) setSelectedEquipment(parsed.selectedEquipment);
        if (parsed.customEquipment) setCustomEquipment(parsed.customEquipment);
        if (parsed.dangerDescription) setDangerDescription(parsed.dangerDescription);
        if (parsed.escapeDescription) setEscapeDescription(parsed.escapeDescription);
        if (parsed.escapePreview) setEscapePreview(parsed.escapePreview);
        if (parsed.generatedImage) setGeneratedImage(parsed.generatedImage);
        if (parsed.preparationPosterSceneName) setPreparationPosterSceneName(parsed.preparationPosterSceneName);
        if (parsed.earnedBadges) setEarnedBadges(parsed.earnedBadges);
        if (parsed.preparationSummary) setPreparationSummary(parsed.preparationSummary);
        // 重新进入时只恢复可持久化的预览数据，忽略旧 blob URL
      } catch (e) {
        console.error('Failed to parse saved progress', e);
      }
    }
  }, [user, assignmentId, sessionId]);

  // 加载任务信息
  useEffect(() => {
    const loadAssignment = async () => {
      try {
        const response = await api.get(`/assignments/${assignmentId}`);
        setBadges(response.data.badges || []);
      } catch (error) {
        console.error('Failed to load assignment', error);
      }
    };
    if (assignmentId) loadAssignment();
  }, [assignmentId]);

  useEffect(() => {
    if (!sessionId || !user) {
      latestSessionDetailRef.current = null;
      isHydratingSessionRef.current = false;
      setIsSessionHydrated(true);
      return;
    }

    let active = true;

    const loadSession = async () => {
      clearPendingDraftSave();

      setIsSessionLoading(true);
      setIsSessionHydrated(false);
      setSessionLoadError('');
      isHydratingSessionRef.current = true;

      try {
        const response = await api.get<AdventureSessionDetailDto>(`/adventure-sessions/${sessionId}`);
        if (!active) {
          return;
        }

        const detail = response.data;
        latestSessionDetailRef.current = detail;

        const avatarStep = detail.steps.find((step) => step.stepKey === 'AVATAR');
        const teamStep = detail.steps.find((step) => step.stepKey === 'TEAM');
        const sceneStep = detail.steps.find((step) => step.stepKey === 'SCENE');
        const equipmentStep = detail.steps.find((step) => step.stepKey === 'EQUIPMENT');
        const dangerStep = detail.steps.find((step) => step.stepKey === 'DANGER');
        const escapeStep = detail.steps.find((step) => step.stepKey === 'ESCAPE');

        setSelectedAvatar((avatarStep?.draftPayload?.selectedAvatar as string) ?? null);
        setSelectedCharA((teamStep?.draftPayload?.selectedCharA as string) ?? null);
        setSelectedCharB((teamStep?.draftPayload?.selectedCharB as string) ?? null);
        setGeneratedImage(
          (equipmentStep?.draftPayload?.generatedImage as string) ??
          (equipmentStep?.draftPayload?.poster as { teamPhotoUrl?: string } | undefined)?.teamPhotoUrl ??
          ''
        );
        setPreparationPosterSceneName(
          ((equipmentStep?.draftPayload?.poster as { sceneName?: string } | undefined)?.sceneName as string) ??
          ((equipmentStep?.draftPayload?.selectedSceneName as string) ?? '')
        );
        setSelectedScene((sceneStep?.draftPayload?.selectedScene as string) ?? null);
        setCustomScene((sceneStep?.draftPayload?.customScene as string) ?? '');
        setSelectedEquipment((equipmentStep?.draftPayload?.selectedEquipment as string[]) ?? []);
        setCustomEquipment((equipmentStep?.draftPayload?.customEquipment as string) ?? '');
        setPreparationSummary((equipmentStep?.draftPayload?.voiceSummary as string) ?? '');
        setDangerDescription((dangerStep?.draftPayload?.dangerDescription as string) ?? '');
        setDangerPreview(
          normalizeDangerPreviewValue(
            dangerStep?.draftPayload?.dangerPreview ??
            dangerStep?.draftPayload?.imageUrl ??
            dangerStep?.draftPayload?.thumbnailUrl ??
            null
          )
        );
        setDangerGuideState(normalizeDangerGuideChatState(dangerStep?.draftPayload?.guideChat ?? null));
        setEscapeDescription((escapeStep?.draftPayload?.escapeDescription as string) ?? '');
        setEscapePreview(
          normalizeDangerPreviewValue(
            escapeStep?.draftPayload?.escapePreview ??
            escapeStep?.draftPayload?.imageUrl ??
            escapeStep?.draftPayload?.thumbnailUrl ??
            null
          )
        );

        setCurrentStep(detail.status === 'COMPLETED' ? Step.COMPLETED : getStepFromStepKey(detail.currentStep));
        setFurthestStepReached(getFurthestAccessibleStepFromSession(detail));

        setStepStatuses({
          AVATAR: avatarStep?.status ?? 'NOT_STARTED',
          TEAM: teamStep?.status ?? 'NOT_STARTED',
          SCENE: sceneStep?.status ?? 'NOT_STARTED',
          EQUIPMENT: equipmentStep?.status ?? 'NOT_STARTED',
          DANGER: dangerStep?.status ?? 'NOT_STARTED',
          ESCAPE: escapeStep?.status ?? 'NOT_STARTED',
        });
        setEarnedBadges(
          getEarnedBadgesFromStepStatuses({
            availableBadges: badges,
            statuses: {
              AVATAR: avatarStep?.status ?? 'NOT_STARTED',
              TEAM: teamStep?.status ?? 'NOT_STARTED',
              SCENE: sceneStep?.status ?? 'NOT_STARTED',
              EQUIPMENT: equipmentStep?.status ?? 'NOT_STARTED',
              DANGER: dangerStep?.status ?? 'NOT_STARTED',
              ESCAPE: escapeStep?.status ?? 'NOT_STARTED',
            },
          })
        );
        setStepFeedbacks({
          AVATAR: normalizeStoredFeedback(avatarStep?.aiComment ?? null),
          TEAM: normalizeStoredFeedback(teamStep?.aiComment ?? null),
          SCENE: normalizeStoredFeedback(sceneStep?.aiComment ?? null),
          EQUIPMENT: normalizeStoredFeedback(equipmentStep?.aiComment ?? null),
          DANGER: normalizeStoredFeedback(dangerStep?.aiComment ?? null),
          ESCAPE: normalizeStoredFeedback(escapeStep?.aiComment ?? null),
        });
        setSaveState('saved');
      } catch (error) {
        if (active) {
          setSessionLoadError('读取探险记录失败，请返回任务中心重新进入。');
        }
      } finally {
        if (active) {
          setIsSessionLoading(false);
          setIsSessionHydrated(true);
          window.setTimeout(() => {
            isHydratingSessionRef.current = false;
          }, 0);
        }
      }
    };

    loadSession();

    return () => {
      active = false;
    };
  }, [sessionId, user]);

  useEffect(() => {
    if (hasRestoredRewardModalRef.current) {
      return;
    }

    if (!locationState?.reopenRewardModal || !locationState.rewardStepKey) {
      return;
    }

    if (!isSessionHydrated || !badges.length) {
      return;
    }

    const restoredStep = getStepFromStepKey(locationState.rewardStepKey);
    setRewardModal({
      show: true,
      badge: findBadgeForAdventureStep(badges, locationState.rewardStepKey),
      photo: restoredStep === Step.SELECT_EQUIPMENT ? generatedImage : undefined,
      step: restoredStep,
    });
    hasRestoredRewardModalRef.current = true;
  }, [badges, generatedImage, isSessionHydrated, locationState]);

  useEffect(() => {
    if (!locationState?.returnStepKey || !isSessionHydrated) {
      return;
    }

    const restoreToken = `${location.key}:${locationState.returnStepKey}`;
    if (restoredReturnStepRef.current === restoreToken) {
      return;
    }

    const restoredStep = getStepFromStepKey(locationState.returnStepKey);
    if (restoredStep > furthestStepReached && furthestStepReached !== Step.COMPLETED) {
      return;
    }

    setCurrentStep(restoredStep);
    restoredReturnStepRef.current = restoreToken;
  }, [furthestStepReached, isSessionHydrated, location.key, locationState]);

  useEffect(() => {
    const normalizedCurrentStep = currentStep === Step.COMPLETED ? Step.WRITE_ESCAPE : currentStep;
    setFurthestStepReached((prev) => (normalizedCurrentStep > prev ? normalizedCurrentStep : prev));
  }, [currentStep]);

  useEffect(() => {
    if (!rewardModal?.show) {
      return;
    }

    const timer = window.setTimeout(() => {
      setShowConfetti(true);
      window.setTimeout(() => setShowConfetti(false), 4500);
    }, 180);

    return () => {
      window.clearTimeout(timer);
    };
  }, [rewardModal]);

  useEffect(() => {
    if (!sessionId || !badges.length) {
      return;
    }

    setEarnedBadges(
      getEarnedBadgesFromStepStatuses({
        availableBadges: badges,
        statuses: stepStatuses,
      })
    );
  }, [badges, sessionId, stepStatuses]);

  // 清理语音识别实例
  useEffect(() => {
    return () => {
      clearPendingDraftSave();
    };
  }, []);

  const persistStepDraft = async (stepKey: AdventureStepKey, current: Step, immediatePayload?: Record<string, unknown>) => {
    if (!sessionId || !user) {
      return;
    }

    try {
      setSaveState('saving');
      setSaveError('');
      const response = await api.put(`/adventure-sessions/${sessionId}/steps/${stepKey}/draft`, {
        draftPayload: immediatePayload ?? getDraftPayloadForStep(stepKey),
        currentStep: STEP_KEY_BY_STEP[current],
      });
      const savedStatus = response.data?.status as AdventureStepStatus | undefined;
      setStepStatuses((prev) => ({
        ...prev,
        [stepKey]:
          (prev[stepKey] === 'COMPLETED' || prev[stepKey] === 'STALE') &&
          (savedStatus ?? 'IN_PROGRESS') === 'IN_PROGRESS'
            ? prev[stepKey]
            : savedStatus ?? 'IN_PROGRESS',
      }));
      setSaveState('saved');
    } catch (error) {
      setSaveState('error');
      setSaveError('自动保存失败，请检查网络后重试。');
    }
  };

  const scheduleDraftSave = (stepKey: AdventureStepKey, current: Step, immediatePayload?: Record<string, unknown>) => {
    if (!sessionId || !isSessionHydrated || isHydratingSessionRef.current) {
      return;
    }

    clearPendingDraftSave();
    saveTimerRef.current = window.setTimeout(() => {
      persistStepDraft(stepKey, current, immediatePayload);
    }, 1200);
  };

  useEffect(() => {
    if (
      !sessionId ||
      !isSessionHydrated ||
      isHydratingSessionRef.current ||
      currentStep === Step.COMPLETED ||
      isSubmitting ||
      isRecognizingDanger ||
      isRecognizingEscape
    ) {
      return;
    }

    scheduleDraftSave(currentStepKey, currentStep);
  }, [
    isSessionHydrated,
    sessionId,
    currentStep,
    currentStepKey,
    selectedAvatar,
    selectedCharA,
    selectedCharB,
    selectedScene,
    customScene,
    selectedEquipment,
    customEquipment,
    preparationSummary,
    dangerDescription,
    dangerPreview,
    escapeDescription,
    escapePreview,
    generatedImage,
    isSubmitting,
    isRecognizingDanger,
    isRecognizingEscape,
  ]);

  useEffect(() => {
    if (currentStep !== Step.COMPLETED || generatedImage || isRebuildingCompletedPhotoRef.current) {
      return;
    }

    if (!selectedAvatar || !selectedCharA || !selectedCharB) {
      return;
    }

    isRebuildingCompletedPhotoRef.current = true;
    void generateTeamPhoto().finally(() => {
      isRebuildingCompletedPhotoRef.current = false;
    });
  }, [currentStep, generatedImage, selectedAvatar, selectedCharA, selectedCharB]);

  // 提交当前步骤
  const submitCurrentStep = async ({ overrideDangerDescription }: { overrideDangerDescription?: string } = {}) => {
    const finalDangerDescription = overrideDangerDescription ?? dangerDescription;

    if (isSubmitting) return;

    // 验证必填项
    if (currentStep === Step.DESIGN_DANGER) {
      if (!finalDangerDescription.trim()) return;
    } else if (currentStep === Step.SELECT_EQUIPMENT) {
      if (!preparationSummary.trim()) return;
      if (!selectedAvatar) {
        alert('探险形象信息丢失，请重新选择探险形象后再提交。');
        setCurrentStep(Step.SELECT_AVATAR);
        return;
      }
      if (!selectedCharA || !selectedCharB) {
        alert('探险队友信息丢失，请重新确认队友后再生成出发海报。');
        setCurrentStep(Step.SELECT_CHARACTERS);
        return;
      }
      if (!selectedSceneName) {
        alert('探险场景信息丢失，请重新选择场景后再生成出发海报。');
        setCurrentStep(Step.SELECT_SCENE);
        return;
      }
    }

    if (summaryTranscriptionStatus === 'recording') {
      cancelSummaryRecording();
    }
    if (customEquipmentTranscriptionStatus === 'recording') {
      cancelCustomEquipmentRecording();
    }

    clearPendingDraftSave();
    setIsSubmitting(true);
    setFeedback(null);

    try {
      let content = '';
      let badgeId = '';

      if (currentStep === Step.SELECT_EQUIPMENT) {
        const response = await api.post('/submissions/preparation-complete', {
          assignmentId,
          avatarId: selectedAvatar,
          teammateIds: [selectedCharA, selectedCharB].filter(Boolean),
          teammateNames: [selectedCharAName, selectedCharBName],
          sceneId: selectedScene === 'custom' ? undefined : selectedScene ?? undefined,
          sceneName: selectedSceneName,
          isCustomScene: selectedScene === 'custom',
          equipmentIds: selectedEquipment,
          equipmentNames: selectedEquipmentNames,
          customEquipment,
          voiceSummary: preparationSummary,
        });

        const { aiFeedback, badge, poster, submission } = response.data;
        setFeedback(aiFeedback);
        setPreparationPosterSceneName((poster?.sceneName as string) ?? selectedSceneName);
        setStepFeedbacks((prev) => ({
          ...prev,
          EQUIPMENT: aiFeedback,
        }));

        if (badge) {
          setEarnedBadges((prev) =>
            prev.some((item) => item.id === badge.id) ? prev : [...prev, badge]
          );
        }

        const photoUrl = generatedImage || await generateTeamPhoto();

        if (sessionId && submission?.id) {
          await api.post(`/adventure-sessions/${sessionId}/preparation/complete`, {
            submissionId: submission.id,
            aiFeedback,
            draftPayload: {
              ...getTeamDraftPayload(),
              ...getDraftPayloadForStep('SCENE'),
              ...getDraftPayloadForStep('EQUIPMENT'),
              poster,
              generatedImage: photoUrl,
            },
          });

          setStepStatuses((prev) => ({
            ...prev,
            TEAM: 'COMPLETED',
            SCENE: 'COMPLETED',
            EQUIPMENT: 'COMPLETED',
          }));
        }

        setRewardModal({
          show: true,
          badge,
          photo: photoUrl,
          step: currentStep,
        });
        return;
      } else if (currentStep === Step.DESIGN_DANGER) {
        content = `险情设计：${finalDangerDescription}。对应场景：${selectedScene === 'custom' ? customScene : SCENES.find(s => s.id === selectedScene)?.name}`;
        badgeId = findBadgeForAdventureStep(badges, 'DANGER')?.id || '';
      }

      if (!badgeId) {
        alert('未找到对应勋章，请联系老师');
        setIsSubmitting(false);
        return;
      }

      const response = await api.post('/submissions', {
        assignmentId,
        badgeId,
        content,
      });

      const { aiFeedback } = response.data;
      setFeedback(aiFeedback);
      setStepFeedbacks((prev) => ({
        ...prev,
        [currentStepKey]: aiFeedback,
      }));

      if (aiFeedback.passed) {
        const unlockedBadge = badges.find(b => b.id === badgeId);
        if (unlockedBadge) {
          setEarnedBadges((prev) =>
            prev.some((badge) => badge.id === unlockedBadge.id) ? prev : [...prev, unlockedBadge]
          );
        }

        if (unlockedBadge) {
          setRewardModal({
            show: true,
            badge: unlockedBadge,
            step: currentStep,
          });
        }
      }
    } catch (error) {
      console.error('Submission failed', error);
      alert('提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStepSubmit = async () => {
    await submitCurrentStep();
  };

  const handleSceneSelect = (id: string) => {
    setSelectedScene(id);
    setGeneratedImage('');
    setPreparationPosterSceneName('');
    markDependentStepsStale('SCENE');
    const payload = {
      selectedScene: id,
      customScene,
      selectedSceneName: SCENES.find((scene) => scene.id === id)?.name ?? '',
    };
    void persistStepDraft('SCENE', Step.SELECT_EQUIPMENT, payload);
    // 场景选择不需要理由，直接进入装备选择
    if (id !== 'custom') {
      setTimeout(() => setCurrentStep(Step.SELECT_EQUIPMENT), 500);
    }
  };

  const handleCustomSceneSubmit = () => {
    if (customScene.trim()) {
      setGeneratedImage('');
      setPreparationPosterSceneName('');
      markDependentStepsStale('SCENE');
      void persistStepDraft('SCENE', Step.SELECT_EQUIPMENT, {
        selectedScene: 'custom',
        customScene,
        selectedSceneName: customScene.trim(),
      });
      setSelectedScene('custom');
      setCurrentStep(Step.SELECT_EQUIPMENT);
    }
  }

  const generateTeamPhoto = (): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve('');
        return;
      }

      const selectedSceneAsset =
        selectedScene && selectedScene !== 'custom'
          ? SCENES.find((scene) => scene.id === selectedScene)?.image ?? ''
          : '';

      const drawFallbackBackdrop = () => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, '#dbeafe');
        gradient.addColorStop(1, '#93c5fd');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 600, 400);
        ctx.fillStyle = 'rgba(255,255,255,0.18)';
        ctx.fillRect(20, 20, 560, 360);
      };

      const drawBackdrop = () =>
        new Promise<void>((backdropResolve) => {
          drawFallbackBackdrop();

          if (!selectedSceneAsset) {
            backdropResolve();
            return;
          }

          const background = new Image();
          background.crossOrigin = 'Anonymous';
          background.src = selectedSceneAsset;
          background.onload = () => {
            ctx.drawImage(background, 0, 0, 600, 400);
            ctx.fillStyle = 'rgba(15, 23, 42, 0.28)';
            ctx.fillRect(0, 0, 600, 400);
            backdropResolve();
          };
          background.onerror = () => {
            backdropResolve();
          };
        });

      const drawPosterHeader = () => {
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.fillRect(28, 20, 544, 70);
        ctx.fillStyle = '#0f172a';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('我们的探险小队', 300, 50);
        ctx.fillStyle = '#1d4ed8';
        ctx.font = 'bold 15px Arial';
        ctx.fillText(`探险场景：${selectedSceneName || '待定'}`, 300, 74);
      };

      const loadAndDrawImage = (src: string, frameX: number, frameY: number, label: string) => {
        return new Promise<void>((imgResolve) => {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.src = src;
          img.onload = () => {
            const frameWidth = 140;
            const frameHeight = 170;
            const rect = getContainedImageRect({
              sourceWidth: img.width,
              sourceHeight: img.height,
              frameX,
              frameY,
              frameWidth,
              frameHeight,
            });

            ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
            ctx.fillStyle = '#333';
            ctx.font = 'bold 13px Arial';
            const lines = splitCanvasLabelLines(label);
            const lineHeight = 16;
            const textBaseY = frameY + frameHeight + 18;

            lines.forEach((line, index) => {
              const offset = lines.length > 1 ? index * lineHeight : 8;
              ctx.fillText(line, frameX + frameWidth / 2, textBaseY + offset);
            });
            imgResolve();
          };
          img.onerror = () => imgResolve();
        });
      };

      const myAvatar = AVATARS.find(a => a.id === selectedAvatar)?.url || '';
      const charA = CHARACTERS_COL_A.find(c => c.id === selectedCharA);
      const charB = CHARACTERS_COL_B.find(c => c.id === selectedCharB);
      const studentName = getTeamMemberLabel(user?.username);

      drawBackdrop()
        .then(() => {
          drawPosterHeader();
          return Promise.all([
            loadAndDrawImage(myAvatar, 60, 120, studentName),
            loadAndDrawImage(charA?.image || '', 230, 120, charA?.name || ''),
            loadAndDrawImage(charB?.image || '', 400, 120, charB?.name || ''),
          ]);
        })
        .then(() => {
          const url = canvas.toDataURL();
          setGeneratedImage(url);
          setPreparationPosterSceneName(selectedSceneName);
          resolve(url);
        });
    });
  };

  const handleCloseReward = () => {
    const rewardSourceStep = rewardModal?.step ?? currentStep;
    setRewardModal(null);
    setFeedback(null);

    if (rewardSourceStep === Step.SELECT_CHARACTERS) {
      setCurrentStep(Step.SELECT_SCENE);
    } else if (rewardSourceStep === Step.SELECT_EQUIPMENT) {
      setCurrentStep(Step.DESIGN_DANGER);
    } else if (rewardSourceStep === Step.DESIGN_DANGER) {
      setCurrentStep(Step.WRITE_ESCAPE);
    } else if (rewardSourceStep === Step.WRITE_ESCAPE) {
      setCurrentStep(Step.COMPLETED);
    }
  };

  const handleRetryRewardStep = () => {
    if (!rewardModal || (rewardModal.step !== Step.DESIGN_DANGER && rewardModal.step !== Step.WRITE_ESCAPE)) {
      return;
    }

    const stepKey = STEP_KEY_BY_STEP[rewardModal.step];

    setRewardModal(null);
    setFeedback(null);
    setStepFeedbacks((prev) => ({
      ...prev,
      [stepKey]: null,
    }));
    setStepStatuses((prev) => ({
      ...prev,
      [stepKey]: 'IN_PROGRESS',
    }));

    if (rewardModal.step === Step.DESIGN_DANGER) {
      setCurrentStep(Step.DESIGN_DANGER);
      return;
    }

    setCurrentStep(Step.WRITE_ESCAPE);
  };

  const handleOpenPeerGallery = (galleryType?: 'danger' | 'escape') => {
    if (!assignmentId) {
      return;
    }

    const rewardStepKey = rewardModal ? STEP_KEY_BY_STEP[rewardModal.step] : null;
    const returnStepKey =
      rewardStepKey ??
      (currentStep !== Step.COMPLETED && (currentStepKey === 'DANGER' || currentStepKey === 'ESCAPE')
        ? currentStepKey
        : null);
    const targetGalleryType =
      galleryType ?? ((rewardModal?.step ?? currentStep) === Step.WRITE_ESCAPE ? 'escape' : 'danger');
    const returnTo = `/student/adventure/${assignmentId}${sessionId ? `?sessionId=${sessionId}` : ''}`;

    navigate(`/student/adventure/${assignmentId}/gallery/${targetGalleryType}`, {
      state: {
        returnTo,
        returnState: returnStepKey
          ? {
            returnStepKey,
            ...(rewardStepKey && (rewardModal?.step === Step.DESIGN_DANGER || rewardModal?.step === Step.WRITE_ESCAPE)
              ? {
                reopenRewardModal: true,
                rewardStepKey,
              }
              : {}),
          }
          : undefined,
        preferredSceneGroup:
          targetGalleryType === 'danger' ? getSceneGroupBySelection(selectedScene, customScene) : undefined,
      },
    });
  };

  const renderPeerGalleryJumpButton = (galleryType: 'danger' | 'escape') => (
    <button
      type="button"
      onClick={() => handleOpenPeerGallery(galleryType)}
      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
    >
      {galleryType === 'danger' ? '去看看同学的险情设计' : '去看看同学的逃生故事'}
    </button>
  );

  const handleExitAdventure = async () => {
    clearPendingDraftSave();

    if (sessionId && currentStep !== Step.COMPLETED) {
      await persistStepDraft(currentStepKey, currentStep);
    }

    navigate('/student');
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearPendingDraftSave();
    setFeedback(null);
    setFeedback(null);
    setIsRecognizingDanger(true);

    const formData = new FormData();
    const uploadFile = await prepareDangerUploadFile(file);
    const previewDataUrl = await readFileAsDataUrl(uploadFile);
    setDangerPreview(previewDataUrl);
    formData.append('image', uploadFile);
    formData.append('assignmentId', assignmentId || '');
    formData.append('sceneName', sceneSummaryName);

    try {
      const response = await api.post('/submissions/danger-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const { aiFeedback } = response.data;
      setDangerDescription('');
      setFeedback(aiFeedback);
      setStepFeedbacks((prev) => ({
        ...prev,
        DANGER: aiFeedback,
      }));

      if (aiFeedback.passed) {
        const unlockedBadge = findBadgeByNames(badges, ADVENTURE_BADGE_ALIASES.danger);
        if (unlockedBadge) {
          setEarnedBadges((prev) =>
            prev.some((badge) => badge.id === unlockedBadge.id) ? prev : [...prev, unlockedBadge]
          );
          if (sessionId && response.data?.submission?.id) {
            await api.post(`/adventure-sessions/${sessionId}/steps/DANGER/attach`, {
              submissionId: response.data.submission.id,
              aiFeedback,
              nextStep: 'ESCAPE',
              imageUrl: response.data.imageUrl,
              thumbnailUrl: response.data.thumbnailUrl,
            });
            setStepStatuses((prev) => ({
              ...prev,
              DANGER: 'COMPLETED',
            }));
          }
          setRewardModal({
            show: true,
            badge: unlockedBadge,
            step: currentStep,
          });
        }
      }
    } catch (error) {
      console.error('Danger image submission failed', error);
      setDangerDescription('');
      setFeedback({
        passed: false,
        score: 0,
        strengths: [],
        improvements: [],
        encouragement: getDangerUploadErrorMessage(error),
      });
    } finally {
      setIsRecognizingDanger(false);
      event.target.value = '';
    }
  };

  const handleDangerGuideSubmit = async () => {
    if (!sessionId || !dangerGuideDraft.trim() || isDangerGuideSubmitting) {
      return;
    }

    if (dangerGuideTranscriptionStatus === 'recording') {
      cancelDangerGuideRecording();
    }

    setIsDangerGuideSubmitting(true);
    setDangerGuideError('');

    const trimmedMessage = dangerGuideDraft.trim();
    const clientTurns = dangerGuideState?.turns ?? [];

    try {
      const response = await api.post(`/adventure-sessions/${sessionId}/steps/DANGER/guide-chat`, {
        message: trimmedMessage,
        inputMode: dangerGuideLastInputMode,
        includeDangerImage: Boolean(dangerPreview),
        clientTurns,
      });

      const nextState = normalizeDangerGuideChatState({
        ...(response.data?.conversationState ?? {}),
        teacherSummary: response.data?.teacherSummary,
        lastInputMode: dangerGuideLastInputMode,
        lastAnswer: response.data?.answer,
        verdict: response.data?.verdict,
        sciencePoints: response.data?.sciencePoints,
        suggestion: response.data?.suggestion,
        updatedAt: response.data?.teacherSummary?.updatedAt,
      });

      setDangerGuideState(nextState);
      setDangerGuideDraft('');
      setDangerGuideLastInputMode('text');
    } catch (error) {
      console.error('Danger guide chat failed', error);
      setDangerGuideError('智多星暂时没接上，请稍后再试。');
    } finally {
      setIsDangerGuideSubmitting(false);
    }
  };

  const handleEscapeImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearPendingDraftSave();
    setFeedback(null);
    setIsRecognizingEscape(true);

    const formData = new FormData();
    const uploadFile = await prepareDangerUploadFile(file);
    const previewDataUrl = await readFileAsDataUrl(uploadFile);
    setEscapePreview(previewDataUrl);
    formData.append('image', uploadFile);
    formData.append('assignmentId', assignmentId || '');
    formData.append('sceneName', sceneSummaryName);

    try {
      const response = await api.post('/submissions/escape-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const { aiFeedback } = response.data;
      setEscapeDescription('');
      setFeedback(aiFeedback);
      setStepFeedbacks((prev) => ({
        ...prev,
        ESCAPE: aiFeedback,
      }));

      if (aiFeedback.passed) {
        const unlockedBadge = findBadgeByNames(badges, ADVENTURE_BADGE_ALIASES.escape);
        if (unlockedBadge) {
          setEarnedBadges((prev) =>
            prev.some((badge) => badge.id === unlockedBadge.id) ? prev : [...prev, unlockedBadge]
          );
          if (sessionId && response.data?.submission?.id) {
            await api.post(`/adventure-sessions/${sessionId}/steps/ESCAPE/attach`, {
              submissionId: response.data.submission.id,
              aiFeedback,
              nextStep: 'ESCAPE',
              imageUrl: response.data.imageUrl,
              thumbnailUrl: response.data.thumbnailUrl,
            });
            setStepStatuses((prev) => ({
              ...prev,
              ESCAPE: 'COMPLETED',
            }));
          }
          setRewardModal({
            show: true,
            badge: unlockedBadge,
            step: currentStep,
          });
        }
      }
    } catch (error) {
      console.error('Escape image submission failed', error);
      setEscapeDescription('');
      setFeedback({
        passed: false,
        score: 0,
        strengths: [],
        improvements: [],
        encouragement: getDangerUploadErrorMessage(error),
      });
    } finally {
      setIsRecognizingEscape(false);
      event.target.value = '';
    }
  };

  // 渲染助手
  const renderAvatarSelection = () => (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-xl font-bold text-gray-800">0. 选择你的探险形象</h2>
      <div className="space-y-4">
        {(['boys', 'girls'] as const).map((group) => (
          <div
            key={group}
            data-testid="avatar-group"
            className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 md:p-5"
          >
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {AVATARS.filter((avatar) => avatar.group === group).map((avatar) => (
                <button
                  key={avatar.id}
                  type="button"
                  aria-label={`选择头像 ${avatar.label}`}
                  onClick={() => {
                    setSelectedAvatar(avatar.id);
                    setGeneratedImage('');
                    setPreparationPosterSceneName('');
                    void persistStepDraft('AVATAR', Step.SELECT_CHARACTERS, {
                      selectedAvatar: avatar.id,
                      avatarLabel: avatar.label,
                    });
                    setTimeout(() => setCurrentStep(Step.SELECT_CHARACTERS), 300);
                  }}
                  className={`relative rounded-2xl border-2 p-2.5 transition-all duration-200 ${selectedAvatar === avatar.id
                    ? 'border-indigo-500 bg-indigo-50 shadow-md scale-[1.03]'
                    : 'border-transparent bg-white hover:border-indigo-200 hover:shadow-sm'
                    }`}
                >
                  <img
                    src={avatar.url}
                    alt={avatar.label}
                    className="mx-auto h-24 w-full object-contain md:h-28"
                  />
                  {selectedAvatar === avatar.id && (
                    <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-xs font-bold text-white shadow-sm">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCharacterSelection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">1. 选择你的探险队友</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-orange-50 p-4 rounded-xl">
          <h3 className="font-bold text-orange-800 mb-3">第一列：专业人士</h3>
          <div className="space-y-3">
            {CHARACTERS_COL_A.map(char => (
              <button
                key={char.id}
                onClick={() => {
                  setSelectedCharA(char.id);
                  setGeneratedImage('');
                  setPreparationPosterSceneName('');
                  markDependentStepsStale('TEAM');
                }}
                className={`w-full rounded-2xl border-2 p-3 transition-all ${selectedCharA === char.id ? 'border-orange-400 bg-orange-200 shadow-sm' : 'border-transparent bg-white hover:bg-orange-100'
                  }`}
              >
                <div className="flex items-center gap-3 text-left">
                  <img
                    src={char.image}
                    alt={char.name}
                    className="h-20 w-16 flex-none object-contain"
                  />
                  <span className="font-medium text-gray-800">{char.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-xl">
          <h3 className="font-bold text-blue-800 mb-3">第二列：伙伴</h3>
          <div className="space-y-3">
            {CHARACTERS_COL_B.map(char => (
              <button
                key={char.id}
                onClick={() => {
                  setSelectedCharB(char.id);
                  setGeneratedImage('');
                  setPreparationPosterSceneName('');
                  markDependentStepsStale('TEAM');
                }}
                className={`w-full rounded-2xl border-2 p-3 transition-all ${selectedCharB === char.id ? 'border-blue-400 bg-blue-200 shadow-sm' : 'border-transparent bg-white hover:bg-blue-100'
                  }`}
              >
                <div className="flex items-center gap-3 text-left">
                  <img
                    src={char.image}
                    alt={char.name}
                    className="h-20 w-16 flex-none object-contain"
                  />
                  <span className="font-medium text-gray-800">{char.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedCharA && selectedCharB && (
        <div className="mt-6 animate-fade-in rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <button
            onClick={() => {
              void persistStepDraft('TEAM', Step.SELECT_SCENE, getTeamDraftPayload());
              setCurrentStep(Step.SELECT_SCENE);
            }}
            className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white shadow-md transition hover:bg-indigo-700"
          >
            确认队友，继续准备
          </button>
        </div>
      )}
    </div>
  );

  const renderSceneSelection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">2. 选择探险场景</h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5 md:gap-3">
        {SCENES.map(scene => (
          <button
            key={scene.id}
            onClick={() => handleSceneSelect(scene.id)}
            className="overflow-hidden rounded-2xl border-2 border-gray-100 bg-white text-left transition-all hover:border-indigo-400 hover:bg-indigo-50"
          >
            <div className="aspect-[4/5] w-full overflow-hidden bg-gray-100">
              <img
                src={scene.image}
                alt={scene.name}
                data-testid="scene-image"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="px-3 py-3">
              <span className="block text-sm font-semibold text-gray-800 md:text-lg">{scene.name}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-4">
        <h3 className="text-sm font-bold text-gray-500 mb-2">或自定义场景：</h3>
        <div className="relative flex space-x-2">
          <input
            type="text"
            value={customScene}
            onChange={(e) => {
              if (customSceneTranscriptionStatus === 'recording') {
                cancelCustomSceneRecording();
              }
              setCustomScene(e.target.value);
            }}
            className="flex-1 rounded-lg border border-gray-300 p-3 pr-16"
            placeholder="输入你想去的地方..."
          />
          <div className="absolute right-[7.25rem] top-1/2 -translate-y-1/2">
            <PressToTalkButton
              disabled={!isCustomSceneSpeechSupported}
              status={customSceneTranscriptionStatus}
              onStart={startCustomSceneRecording}
              onStop={stopCustomSceneRecording}
            />
          </div>
          <button
            onClick={handleCustomSceneSubmit}
            disabled={!customScene.trim()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-gray-300"
          >
            确定
          </button>
        </div>
        {customSceneTranscriptionError ? <p className="mt-2 text-sm text-red-500">{customSceneTranscriptionError}</p> : null}
      </div>
    </div>
  );

  const renderEquipmentSelection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">3. 选择探险装备</h2>
      <div className="p-4 bg-indigo-50 rounded-lg mb-4">
        当前场景：<span className="font-bold text-indigo-700">
          {selectedScene === 'custom' ? customScene : SCENES.find(s => s.id === selectedScene)?.name}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {EQUIPMENT.map(item => (
          <button
            key={item.id}
            onClick={() => {
              markDependentStepsStale('EQUIPMENT');
              setSelectedEquipment(prev =>
                prev.includes(item.id) ? prev.filter(i => i !== item.id) : [...prev, item.id]
              );
            }}
            className={`p-4 rounded-xl border-2 transition-all flex items-center space-x-3 ${selectedEquipment.includes(item.id)
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200 bg-white hover:border-indigo-300'
              }`}
          >
            <img
              src={item.icon}
              alt=""
              aria-hidden="true"
              data-testid={`equipment-icon-${item.id}`}
              className="h-8 w-8 object-contain"
            />
            <span>{item.name}</span>
          </button>
        ))}
      </div>

      <div className="mt-4">
        <div className="relative">
          <input
            type="text"
            value={customEquipment}
            onChange={(e) => {
              markDependentStepsStale('EQUIPMENT');
              if (customEquipmentTranscriptionStatus === 'recording') {
                cancelCustomEquipmentRecording();
              }
              setCustomEquipment(e.target.value);
            }}
            className="w-full rounded-lg border border-gray-300 p-3 pr-16"
            placeholder="还需要其他装备吗？(选填)"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <PressToTalkButton
              disabled={!isCustomEquipmentSpeechSupported}
              status={customEquipmentTranscriptionStatus}
              onStart={startCustomEquipmentRecording}
              onStop={stopCustomEquipmentRecording}
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          去这个地方探险的目的是什么？为什么选择这些队友和装备？
        </label>
        <div className="relative">
          <textarea
            value={preparationSummary}
            onChange={(e) => {
              setPreparationSummary(e.target.value);
              if (summaryTranscriptionStatus === 'recording') {
                cancelSummaryRecording();
              }
            }}
            className="w-full p-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
            placeholder="点击麦克风说出理由，或直接输入..."
          />
          <div className="absolute bottom-4 right-4">
            <PressToTalkButton
              disabled={!isSummarySpeechSupported}
              status={summaryTranscriptionStatus}
              onStart={startSummaryRecording}
              onStop={stopSummaryRecording}
            />
          </div>
        </div>
        {summaryTranscriptionError ? <p className="mt-2 text-sm text-red-500">{summaryTranscriptionError}</p> : null}
        {customEquipmentTranscriptionError ? <p className="mt-2 text-sm text-red-500">{customEquipmentTranscriptionError}</p> : null}
        <button
          onClick={handleStepSubmit}
          disabled={isSubmitting || !preparationSummary.trim() || selectedEquipmentNames.length === 0}
          className="mt-4 w-full py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {isSubmitting ? '正在生成出发海报...' : '准备完成'}
        </button>
      </div>
    </div>
  );

  const renderDangerGuideWidget = () => {
    if (!sessionId) {
      return null;
    }

    return (
      <>
        <button
          type="button"
          aria-label="打开智多星的求生锦囊"
          onClick={() => setIsDangerGuideOpen(true)}
          className="fixed bottom-6 right-6 z-30 flex items-center gap-3 rounded-full bg-gradient-to-r from-amber-300 via-orange-300 to-sky-300 px-4 py-3 text-left shadow-[0_16px_40px_rgba(15,23,42,0.18)] transition hover:scale-[1.02]"
        >
          <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-inner">
            <img src={guideStarImage} alt="" aria-hidden="true" className="h-14 w-14 object-contain drop-shadow-sm" />
            <span className="absolute -bottom-1 -right-1 text-sm">✨</span>
          </span>
          <span className="max-w-[9rem] text-sm font-black leading-5 text-slate-900">智多星的求生锦囊</span>
        </button>

        <AnimatePresence>
          {isDangerGuideOpen && (
            <>
              <motion.button
                type="button"
                aria-label="关闭智多星的求生锦囊"
                onClick={() => setIsDangerGuideOpen(false)}
                className="fixed inset-0 z-30 bg-slate-900/25"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
              <motion.aside
                className="fixed bottom-0 right-0 z-40 flex max-h-[80vh] w-full flex-col rounded-t-[2rem] border border-stone-200 bg-white shadow-2xl md:bottom-6 md:right-6 md:w-[24rem] md:rounded-[2rem]"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 24 }}
              >
                <div className="flex items-start justify-between gap-4 border-b border-stone-100 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-sky-200">
                      <img src={guideStarImage} alt="" aria-hidden="true" className="h-11 w-11 object-contain" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900">智多星的求生锦囊</h3>
                    </div>
                  </div>
                  <button type="button" onClick={() => setIsDangerGuideOpen(false)} className="text-sm font-semibold text-slate-500">
                    收起
                  </button>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
                  {dangerGuideState?.turns.length ? (
                    dangerGuideState.turns.map((turn, index) => (
                      <div
                        key={`${turn.role}-${index}-${turn.content}`}
                        className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
                          turn.role === 'user'
                            ? 'ml-10 bg-slate-100 text-slate-700'
                            : 'mr-6 border border-amber-100 bg-amber-50 text-slate-800'
                        }`}
                      >
                        {turn.content}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-4 py-5 text-sm leading-6 text-slate-500">
                      说说你设计了什么险情，想到的求生办法是什么，我会帮你看看有没有不太科学的地方。
                    </div>
                  )}

                </div>

                <div className="border-t border-stone-100 px-5 py-4">
                  <div className="relative">
                    <textarea
                      value={dangerGuideDraft}
                      onChange={(event) => {
                        setDangerGuideLastInputMode('text');
                        setDangerGuideDraft(event.target.value);
                      }}
                      className="min-h-[112px] w-full rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4 pr-16 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-300 focus:bg-white"
                      placeholder="按住说话或直接输入你的问题..."
                    />
                    <div className="absolute bottom-4 right-4">
                      <PressToTalkButton
                        disabled={!isDangerGuideSpeechSupported || isDangerGuideSubmitting}
                        status={dangerGuideTranscriptionStatus}
                        onStart={startDangerGuideRecording}
                        onStop={stopDangerGuideRecording}
                      />
                    </div>
                  </div>
                  {dangerGuideTranscriptionError ? <p className="mt-2 text-sm text-red-500">{dangerGuideTranscriptionError}</p> : null}
                  {dangerGuideError ? <p className="mt-2 text-sm text-red-500">{dangerGuideError}</p> : null}
                  <button
                    type="button"
                    aria-label="发送给探险智多星"
                    onClick={handleDangerGuideSubmit}
                    disabled={!dangerGuideDraft.trim() || isDangerGuideSubmitting}
                    className="mt-3 w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-stone-300"
                  >
                    {isDangerGuideSubmitting ? '智多星思考中...' : '发送给探险智多星'}
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </>
    );
  };

  const renderDangerDesign = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">4. 设计惊险情节</h2>
      {stepStatuses.DANGER === 'COMPLETED' && (
        <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm leading-6 text-sky-900">这一步已经完成了，想回头看看同学怎么设计险情，可以直接从这里跳过去。</p>
            {renderPeerGalleryJumpButton('danger')}
          </div>
        </div>
      )}
      {stepStatuses.DANGER === 'COMPLETED' && stepFeedbacks.DANGER ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
          <div className="text-sm font-semibold text-emerald-800">{AI_COMMENT_LABEL}</div>
          <div className="mt-3">
            {renderFeedbackNarrativeCard(stepFeedbacks.DANGER, {
              compact: true,
              hideTitle: true,
            })}
          </div>
        </div>
      ) : null}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-amber-900">
          探险小队准备出发啦！
          <span className={SUMMARY_HIGHLIGHT_CLASSES.student}>{studentDisplayName}</span>
          已经和
          <span className={SUMMARY_HIGHLIGHT_CLASSES.teammateA}>{selectedCharAName}</span>
          、
          <span className={SUMMARY_HIGHLIGHT_CLASSES.teammateB}>{selectedCharBName}</span>
          组队，决定前往
          <span className={SUMMARY_HIGHLIGHT_CLASSES.scene}>{sceneSummaryName}</span>
          探险，还带上了
          <span className={SUMMARY_HIGHLIGHT_CLASSES.equipment}>{equipmentSummaryText}</span>
          。可是，真正的考验才刚刚开始。快来设计一场惊险的意外吧！
        </p>
      </div>
      <div className="bg-red-50 p-4 rounded-xl border border-red-100">
        <p className="text-red-800">
          巧设险情示意图，用关键词写一写，遇到了什么险情？如何成功求生？请拍照上传你的构思。
        </p>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-500 transition-colors bg-gray-50">
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          disabled={isRecognizingDanger || isSubmitting}
          className="hidden"
          id="danger-upload"
        />
        <label htmlFor="danger-upload" className="cursor-pointer block">
          {dangerPreview ? (
            <div className="relative">
              <img src={dangerPreview} alt="Preview" className="max-h-64 mx-auto rounded-lg shadow-md" />
              <div className="mt-2 text-indigo-600 font-medium">点击重新上传</div>
            </div>
          ) : (
            <div>
              <div className="text-4xl mb-4">📸</div>
              <div className="text-lg font-medium text-gray-700">点击拍照或上传图片</div>
            </div>
          )}
        </label>
      </div>
      {(isRecognizingDanger || isSubmitting) && (
        <div className="rounded-xl bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
          {AI_NAME}正在查看你的险情图，请稍等一下...
        </div>
      )}
      {renderDangerGuideWidget()}
    </div>
  );

  const renderEscapeWriting = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">5. 展开逃生过程</h2>
      {stepStatuses.ESCAPE === 'COMPLETED' && (
        <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm leading-6 text-sky-900">这一步已经完成了，想回头看看同学怎么展开逃生故事，可以直接从这里跳过去。</p>
            {renderPeerGalleryJumpButton('escape')}
          </div>
        </div>
      )}
      {stepStatuses.ESCAPE === 'COMPLETED' && stepFeedbacks.ESCAPE ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
          <div className="text-sm font-semibold text-emerald-800">{AI_COMMENT_LABEL}</div>
          <div className="mt-3">
            {renderFeedbackNarrativeCard(stepFeedbacks.ESCAPE, {
              compact: true,
              hideTitle: true,
            })}
          </div>
        </div>
      ) : null}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
        习作目标：展开丰富而合理的想象，把遇到的困境描写具体，把求生的方法写具体。
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-500 transition-colors bg-gray-50">
        <input
          type="file"
          accept="image/*"
          onChange={handleEscapeImageUpload}
          disabled={isRecognizingEscape || isSubmitting}
          className="hidden"
          id="escape-upload"
        />
        <label htmlFor="escape-upload" className="cursor-pointer block">
          {escapePreview ? (
            <div className="relative">
              <img src={escapePreview} alt="Escape Preview" className="max-h-64 mx-auto rounded-lg shadow-md" />
              <div className="mt-2 text-indigo-600 font-medium">点击重新上传</div>
            </div>
          ) : (
            <div>
              <div className="text-4xl mb-4">📝</div>
              <div className="text-lg font-medium text-gray-700">点击拍照或上传图片</div>
            </div>
          )}
        </label>
      </div>
      {(isRecognizingEscape || isSubmitting) && (
        <div className="rounded-xl bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
          {AI_NAME}正在查看你的逃生过程，请稍等一下...
        </div>
      )}
    </div>
  );

  const renderCompleted = () => (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">🎉</div>
      <h2 className="text-3xl font-bold text-gray-800 mb-4">探险圆满完成！</h2>
      <p className="text-gray-600 text-lg mb-8">你已经完成了队友、装备、险情和逃生全过程，三枚勋章都集齐了。</p>
      <div className="bg-white p-6 rounded-xl shadow-lg inline-block max-w-md w-full">
        <h3 className="font-bold text-gray-500 mb-4 uppercase text-sm">你的探险小队合影</h3>
        {generatedImage ? (
          <img src={generatedImage} alt="Team" className="w-full rounded-lg mb-4" />
        ) : (
          <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center mb-4">
            照片生成中...
          </div>
        )}
        <div className="text-left space-y-2 text-sm text-gray-600">
          <p><strong>队友：</strong> {CHARACTERS_COL_A.find(c => c.id === selectedCharA)?.name} & {CHARACTERS_COL_B.find(c => c.id === selectedCharB)?.name}</p>
          <p><strong>目的地：</strong> {completedPosterSceneName}</p>
        </div>
      </div>
      <div className="mt-8 flex flex-col items-center gap-3">
        {stepStatuses.DANGER === 'COMPLETED' && renderPeerGalleryJumpButton('danger')}
        {stepStatuses.ESCAPE === 'COMPLETED' && (
          renderPeerGalleryJumpButton('escape')
        )}
        <button
          onClick={() => navigate('/student')}
          className="px-8 py-3 bg-gray-800 text-white rounded-full font-bold hover:bg-gray-900"
        >
          返回任务列表
        </button>
      </div>
    </div>
  )

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,#f5fbf2_0%,#edf7f1_45%,#f8fafc_100%)] p-4 md:p-8">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute left-6 top-8 text-3xl text-emerald-200">🧭</div>
        <div className="absolute right-10 top-10 text-3xl text-sky-200">🔎</div>
        <div className="absolute left-8 bottom-10 text-3xl text-amber-200">🍃</div>
        <div className="absolute right-8 bottom-8 text-3xl text-orange-200">👣</div>
      </div>
      {showConfetti && <Confetti numberOfPieces={200} recycle={false} />}

      <header className="max-w-4xl mx-auto mb-8 flex justify-between items-center relative z-10">
        <div className="flex items-center space-x-4">
          <button onClick={() => void handleExitAdventure()} className="text-gray-500 hover:text-gray-700">
            ← 退出探险
          </button>

          {/* 顶部勋章栏 */}
          <div className="flex space-x-2 pl-4 border-l border-gray-300">
            {badgeSlots.map((badge) => (
              <motion.div
                key={badge.id}
                data-testid="badge-slot"
                initial={earnedBadgeIds.has(badge.id) ? { scale: 0 } : false}
                animate={earnedBadgeIds.has(badge.id) ? { scale: 1 } : undefined}
                className={`flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-all ${earnedBadgeIds.has(badge.id)
                  ? 'border-yellow-300 bg-yellow-100'
                  : 'border-gray-200 bg-gray-100'
                  }`}
                title={badge.name}
              >
                <img
                  src={getAdventureBadgeIconSrc(badge.name, badge.icon)}
                  alt=""
                  aria-hidden="true"
                  data-testid={`badge-icon-${badge.id}`}
                  className={`h-5 w-5 object-contain ${earnedBadgeIds.has(badge.id) ? '' : 'opacity-35 grayscale'}`}
                />
              </motion.div>
            ))}
            {badgeSlots.length === 0 && (
              <div className="text-xs text-gray-400 flex items-center">
                勋章位
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {sessionId && (
            saveState === 'saved' ? (
              <div
                aria-label="已保存到云端"
                title="已保存到云端"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-sm"
              >
                <span className="text-base font-black">✓</span>
              </div>
            ) : (
              <div className="text-right">
                <p className={`text-sm font-semibold ${saveState === 'error' ? 'text-rose-600' : 'text-slate-500'
                  }`}>
                  {saveState === 'saving'
                    ? '正在保存'
                    : saveState === 'error'
                      ? saveError || '保存失败'
                      : '云端记录已开启'}
                </p>
              </div>
            )
          )}
          <div className="flex space-x-2">
            {EDITABLE_STEPS.map((step) => {
              const isActive = currentStep === step;
              const isAccessible = step <= getHighestAccessibleStep();
              const status = stepStatuses[STEP_KEY_BY_STEP[step]];
              const isCompleted = status === 'COMPLETED';

              return (
                <button
                  key={step}
                  type="button"
                  aria-label={`进度步骤 ${step}`}
                  disabled
                  aria-disabled="true"
                  className={`h-3 w-10 rounded-full border transition-all md:w-14 ${getStepIndicatorClassName({
                    isActive,
                    isCompleted,
                    isAccessible,
                  })}`}
                  title={STEP_LABEL_BY_STEP[step]}
                />
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-10">
        {isSessionLoading && (
          <div className="mb-5 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-700">
            正在恢复这次探险记录，请稍等一下...
          </div>
        )}
        {sessionLoadError && (
          <div className="mb-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {sessionLoadError}
          </div>
        )}
        {currentStep === Step.SELECT_AVATAR && renderAvatarSelection()}
        {currentStep === Step.SELECT_CHARACTERS && renderCharacterSelection()}
        {currentStep === Step.SELECT_SCENE && renderSceneSelection()}
        {currentStep === Step.SELECT_EQUIPMENT && renderEquipmentSelection()}
        {currentStep === Step.DESIGN_DANGER && renderDangerDesign()}
        {currentStep === Step.WRITE_ESCAPE && renderEscapeWriting()}
        {currentStep === Step.COMPLETED && renderCompleted()}
      </div>

      {showCompletedFeedbackRail && !isFeedbackRailCollapsed && (
        <div className="fixed right-4 top-24 z-30 hidden w-[320px] md:block">
          <div className="rounded-3xl border border-slate-200 bg-white/96 p-5 shadow-2xl backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">已完成</p>
                <h3 className="mt-1 text-lg font-black text-slate-900">{AI_DONE_LABEL}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFeedbackRailCollapsed(true)}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700"
                >
                  收起
                </button>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-3xl">
                  👍
                </div>
              </div>
            </div>
            <div className="mt-3">
              {renderFeedbackNarrativeCard(currentStepFeedback!, {
                compact: shouldCompactCurrentStepFeedback,
              })}
            </div>
            <div className="mt-3 rounded-2xl bg-slate-100 px-4 py-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">当前徽章</p>
              <div className="mt-2 flex items-center justify-center gap-2">
                <img
                  src={getAdventureBadgeIconSrc(getBadgeNameByStepKey(currentStepKey))}
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8 object-contain"
                />
                <p className="text-xl font-black text-slate-900">{getBadgeNameByStepKey(currentStepKey)}</p>
              </div>
            </div>
            {currentStepPreview && (
              <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <img src={currentStepPreview} alt="步骤回看图片" className="h-40 w-full object-cover" />
              </div>
            )}
          </div>
        </div>
      )}

      {showCompletedFeedbackRail && isFeedbackRailCollapsed && (
        <div className="fixed right-4 top-24 z-30 hidden md:block">
          <button
            type="button"
            onClick={() => setIsFeedbackRailCollapsed(false)}
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/96 px-4 py-3 text-sm font-semibold text-slate-700 shadow-xl backdrop-blur transition hover:border-slate-300 hover:bg-white"
          >
            <span className="text-lg" aria-hidden="true">👍</span>
            <span>{AI_EXPAND_LABEL}</span>
          </button>
        </div>
      )}

      {/* AI 反馈弹窗 - 仅在未获得勋章时显示，或者作为全屏奖励的补充 */}
      <AnimatePresence>
        {feedback && !rewardModal && !feedback.passed && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-0 left-0 right-0 p-4 md:p-8 flex justify-center pointer-events-none z-40"
          >
            <div className={`bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full border-l-8 pointer-events-auto ${feedback.passed ? 'border-green-500' : 'border-orange-500'
              }`}>
              <div className="flex justify-between items-start mb-4">
                <h3 className={`text-xl font-bold ${feedback.passed ? 'text-green-700' : 'text-orange-700'}`}>
                  {currentStep === Step.DESIGN_DANGER
                    ? AI_COMMENT_LABEL
                    : feedback.passed
                      ? '🎉 评估通过！'
                      : '💪 还需要补充一点'}
                </h3>
                <button onClick={() => setFeedback(null)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              {currentStep === Step.DESIGN_DANGER ? (
                renderFeedbackNarrativeCard(feedback, { compact: true, hideTitle: true })
              ) : (
                <>
                  {((feedback.strengths ?? []).length > 0 || (feedback.improvements ?? []).length > 0) ? (
                    <>
                      {feedback.recognitionSummary &&
                        feedback.recognitionSummary.trim() &&
                        feedback.recognitionSummary.trim() !== (feedback.shortComment || feedback.encouragement || '').trim() ? (
                        <p className="text-gray-600 text-base mb-3">{feedback.recognitionSummary}</p>
                      ) : null}
                      <p className="text-gray-700 text-lg mb-4">{feedback.shortComment || feedback.encouragement}</p>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        {(feedback.strengths ?? []).length > 0 ? (
                          <div className="mb-2">
                            <span className="font-bold text-green-600 mr-2">亮点:</span>
                            {(feedback.strengths ?? []).join('，')}
                          </div>
                        ) : null}
                        {(feedback.improvements ?? []).length > 0 ? (
                          <div>
                            <span className="font-bold text-orange-600 mr-2">建议:</span>
                            {(feedback.improvements ?? []).join('，')}
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <>
                      {feedback.recognitionSummary &&
                        feedback.recognitionSummary.trim() &&
                        feedback.recognitionSummary.trim() !== (feedback.shortComment || feedback.encouragement || '').trim() ? (
                        <p className="text-gray-600 text-base mb-3">{feedback.recognitionSummary}</p>
                      ) : null}
                      <p className="text-gray-700 text-lg mb-4">{feedback.shortComment || feedback.encouragement}</p>
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 全屏勋章奖励弹窗 */}
      <AnimatePresence>
        {rewardModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            data-testid="reward-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.5, y: 100 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.5, y: -100, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="relative max-h-[84vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5 text-center shadow-2xl md:p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#fff7d1,white_55%)] -z-10" />
              <div className="absolute top-0 left-0 right-0 h-20 bg-amber-100/60 rounded-b-[50%] -z-10" />

              <motion.div
                animate={{ rotate: [0, 8, -8, 0] }}
                transition={{ repeat: Infinity, duration: 2.2 }}
                className="mb-4 text-6xl drop-shadow-xl md:text-7xl"
              >
                👍
              </motion.div>

              {rewardModalFeedback && (
                <div className="mb-4">
                  {renderFeedbackNarrativeCard(rewardModalFeedback, {
                    compact: true,
                    hideTitle: true,
                  })}
                </div>
              )}

              <div className="mb-4 rounded-3xl bg-slate-100 px-4 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">获得徽章</p>
                <div className="mt-2 flex items-center justify-center gap-3">
                  <img
                    src={getAdventureBadgeIconSrc(rewardModal.badge?.name ?? '', rewardModal.badge?.icon)}
                    alt=""
                    aria-hidden="true"
                    className="h-10 w-10 object-contain"
                  />
                  <h3 className="text-[1.8rem] font-black text-slate-900 md:text-[2rem]">{rewardModal.badge?.name}</h3>
                </div>
              </div>

              {rewardModal?.step === Step.SELECT_EQUIPMENT ? (
                <div className="mb-4 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 p-4 text-left shadow-lg">
                  <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-amber-500">出发海报</p>
                  {(rewardModal.photo || generatedImage) ? (
                    <img
                      src={rewardModal.photo || generatedImage}
                      alt="小队合照"
                      className="mt-3 h-44 w-full rounded-2xl object-cover"
                    />
                  ) : null}
                  <div className="mt-4 space-y-2 text-sm text-slate-700">
                    <p><strong>队友：</strong>{selectedCharAName}、{selectedCharBName}</p>
                    <p><strong>场景：</strong>{selectedSceneName}</p>
                    <p><strong>装备：</strong>{selectedEquipmentNames.join('、')}</p>
                    <p><strong>探险思路：</strong>{preparationSummary}</p>
                  </div>
                </div>
              ) : rewardPreviewSrc && (
                <div className="mb-4 rounded-2xl bg-white p-2 shadow-lg">
                  <img
                    src={rewardPreviewSrc}
                    alt="Adventure Preview"
                    className="max-h-60 w-full rounded-xl object-cover"
                  />
                  <p className="mt-2 text-xs text-gray-500 font-mono">
                    {rewardModal?.step === Step.SELECT_CHARACTERS
                      ? '我们的探险小队'
                      : rewardModal?.step === Step.WRITE_ESCAPE
                        ? '这一步的逃生写作'
                        : '这一步的探险设计'}
                  </p>
                </div>
              )}

              {(rewardModal?.step === Step.DESIGN_DANGER || rewardModal?.step === Step.WRITE_ESCAPE) && (
                <>
                  <button
                    type="button"
                    onClick={() => handleOpenPeerGallery(rewardModal?.step === Step.WRITE_ESCAPE ? 'escape' : 'danger')}
                    className="mt-3 w-full rounded-2xl border border-slate-200 bg-white py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    {rewardModal?.step === Step.DESIGN_DANGER
                      ? '去看看同学的险情设计'
                      : '去看看同学的逃生故事'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRetryRewardStep}
                    className="mt-3 w-full rounded-2xl border border-amber-200 bg-amber-50 py-3 text-base font-semibold text-amber-800 shadow-sm transition hover:border-amber-300 hover:bg-amber-100"
                  >
                    重新上传这一步
                  </button>
                </>
              )}
              <button
                onClick={handleCloseReward}
                className="mt-3 w-full rounded-2xl bg-yellow-500 py-3 text-lg font-bold text-white shadow-lg transition hover:scale-[1.01] hover:bg-yellow-600 active:scale-95"
              >
                收入囊中，继续探险！
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
